import React, { useCallback } from 'react'

import { arrayOf, bool, func, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'
import SharedOrderSummary from 'Pods/Shared/OrderSummary/OrderSummary'
import BillingTerm from 'Pods/SubscriptionCheckout/Shared/BillingTerm/BillingTerm'
import LegalAgreement from 'Pods/SubscriptionCheckout/Shared/LegalAgreement/LegalAgreement'
import PlanCard from 'Pods/SubscriptionCheckout/Shared/PlanCard/PlanCard'

import formatPrice from 'Utils/formatPrice'
import roundHumanCount from 'Utils/roundHumanCount'
import { NOOP } from 'Utils/stableDefaults'

import PlanSelector from '../PlanSelector/PlanSelector'
import ItemContext from './Components/ItemContext/ItemContext'
import LegacyPlanCard from './Components/LegacyPlanCard/LegacyPlanCard'

const TRANSLATION_KEY_PREFIX = 'subscription_checkout.order_summary'
const RESTRUCTURE_KEY_PREFIX = 'subscription_checkout.plans_restructure.order_summary'

function OrderSummary({
  locale = 'en',
  planName = null,
  features = null,
  originalPrice = null,
  currentPrice,
  vatAmount = 0,
  onPay = NOOP,
  disabled = true,
  hidePay = false,
  selectedPlanKey = null,
  subscriptionV16Enabled = false,
  subscriptionV17CatalogEnabled = false,
  isPlansRestructure = false,
  isPremium = false,
  premiumRenewalDate = null,
  priceUnit = null,
  plans = null,
  onPlanChange = NOOP,
  catalogValue = null,
  itemContext = null,
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const total = currentPrice + vatAmount

  const isPlanSelectorVisible = Boolean(subscriptionV16Enabled && plans) && !isPlansRestructure
  const isBillingTermVisible = Boolean(isPlansRestructure && plans)
  const selectedPlan = isBillingTermVisible ? plans.find((plan) => plan.key === selectedPlanKey) || plans[0] : null
  const formattedCatalogValue = catalogValue ? roundHumanCount(catalogValue) : ''

  // Legacy Premium-only: the upgrade CTA + the "unlocks immediately" footer note. Essentials keeps the
  // plain pay button and no note.
  const payLabel = translate(
    `${TRANSLATION_KEY_PREFIX}.${isPremium && !isPlansRestructure ? 'pay_button_upgrade' : 'pay_button'}`,
    { amount: formatPrice(total) },
  )
  // Only an Essentials -> Premium upgrade carries a renewal date (Premium unlocks now, the new
  // price starts from the active sub's renewal date). Fresh Premium purchases bill today, so the
  // backend sends no date and the note is hidden.
  const premiumRenewalNote = isPremium && premiumRenewalDate
    ? (
      <span className="cgt-caption">
        {translate(`${TRANSLATION_KEY_PREFIX}.premium_renewal_note`, {
          price: formatPrice(currentPrice),
          date: premiumRenewalDate,
        })}
      </span>
    )
    : null

  const renewalNote = selectedPlan && (
    <span className="checkout-renewal-note cgt-caption">
      {translate(`${RESTRUCTURE_KEY_PREFIX}.${selectedPlan.cadence === 'year' ? 'renews_at' : 'next_billing'}`, {
        price: formatPrice(currentPrice),
      })}
    </span>
  )

  return (
    <SharedOrderSummary
      title={translate(`${TRANSLATION_KEY_PREFIX}.title`)}
      subtotal={currentPrice}
      subtotalLabel={translate(`${TRANSLATION_KEY_PREFIX}.subtotal`)}
      subtotalLabelClassName={isPlansRestructure ? 'cgt-body-base' : undefined}
      vatAmount={vatAmount}
      vatLabel={translate(`${TRANSLATION_KEY_PREFIX}.vat_included`)}
      vatTooltip={translate(`${TRANSLATION_KEY_PREFIX}.vat_tooltip`)}
      total={total}
      totalLabel={translate(isPlansRestructure
        ? `${RESTRUCTURE_KEY_PREFIX}.amount_due_today`
        : `${TRANSLATION_KEY_PREFIX}.amount_due_today`)}
      payLabel={payLabel}
      onPay={onPay}
      disabled={disabled}
      hidePay={hidePay}
      legal={<LegalAgreement withFairUse={isPlansRestructure} locale={locale} />}
      footer={isPlansRestructure ? renewalNote : premiumRenewalNote}
    >
      {itemContext?.thumbnail && itemContext?.modelName && (
        <ItemContext
          thumbnail={itemContext.thumbnail}
          modelName={itemContext.modelName}
          subLabel={translate(`${TRANSLATION_KEY_PREFIX}.item_context.sub_label`)}
        />
      )}
      {isPlanSelectorVisible && (
        <PlanSelector
          plans={plans}
          selectedKey={selectedPlanKey}
          onChange={onPlanChange}
          locale={locale}
        />
      )}
      {isBillingTermVisible ? (
        <PlanCard
          tier={isPremium ? 'premium' : 'essentials'}
          plan={selectedPlan}
          catalogValue={formattedCatalogValue}
          locale={locale}
        />
      ) : (
        <LegacyPlanCard
          locale={locale}
          planName={planName}
          features={features}
          originalPrice={originalPrice}
          currentPrice={currentPrice}
          selectedPlanKey={selectedPlanKey}
          subscriptionV16Enabled={subscriptionV16Enabled}
          subscriptionV17CatalogEnabled={subscriptionV17CatalogEnabled}
          isPremium={isPremium}
          isSinglePlanCard={!isPlanSelectorVisible}
          priceUnit={priceUnit}
          catalogValue={formattedCatalogValue}
        />
      )}
      {isBillingTermVisible && (
        <BillingTerm
          plans={plans}
          selectedKey={selectedPlanKey}
          onChange={onPlanChange}
          locale={locale}
        />
      )}
    </SharedOrderSummary>
  )
}

OrderSummary.propTypes = {
  locale: string,
  planName: string,
  features: arrayOf(string),
  originalPrice: number,
  currentPrice: number.isRequired,
  vatAmount: number,
  onPay: func,
  disabled: bool,
  hidePay: bool,
  selectedPlanKey: string,
  subscriptionV16Enabled: bool,
  subscriptionV17CatalogEnabled: bool,
  isPlansRestructure: bool,
  isPremium: bool,
  premiumRenewalDate: string,
  priceUnit: string,
  plans: arrayOf(shape({ key: string, price: number })),
  onPlanChange: func,
  catalogValue: string,
  itemContext: shape({ thumbnail: string, modelName: string }),
}

export default OrderSummary
